/**
 * NEXT UP — the strip along the bottom of the wall.
 *
 * One line per board: who is on next, and who is marking. It cycles because a
 * pub has more boards than a screen has room for, and because somebody walking
 * past wants to find their own name without waiting for a panel to rotate.
 *
 * NOTHING NEW IS FETCHED. Every board's queue is already on the snapshot —
 * boardState puts the next three pending matches on `upcoming`, in the order
 * the board will play them, and decorate() has already resolved the two player
 * names and the marker. This is the same data the idle board card draws its
 * "Next:" line from, shown a second way.
 *
 * IT IS A TAPE, NOT A LOOP.
 *
 * The obvious implementation — two identical copies of the fixtures and a CSS
 * animation translating the pair by -50% for ever — cannot show a changed
 * fixture without a discontinuity. Every line exists twice on the belt at once,
 * so the only moment the text can be replaced is the instant the loop resets,
 * and replacing it there makes new fixtures appear already halfway across the
 * screen rather than arriving.
 *
 * So the strip is a QUEUE of lines moving right to left at a constant speed. A
 * line that has passed the left edge is dropped and the offset reduced by
 * exactly its width in the same frame — the same pixels in the same places, one
 * fewer element — and a fresh line is appended at the tail, off the right edge,
 * from whatever the fixtures say NOW. New content therefore enters the way
 * everything else does: it scrolls in.
 *
 * Motion is written straight to `style.transform` in the animation frame rather
 * than through React state, so a screen that runs all night re-renders only
 * when a line is actually recycled — a few times a minute, not sixty times a
 * second.
 */

import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';

/**
 * Reading pace, in pixels per second.
 *
 * Constant, deliberately: the speed a line crosses the room is a property of
 * how fast it can be read, not of how many boards the venue happens to own.
 * Sizing the loop to the content — the usual marquee trick — makes four boards
 * crawl and twelve race.
 */
const PX_PER_SECOND = 100;

/** Keep this much tape queued past the right edge, so it is never empty. */
const LOOKAHEAD_PX = 600;

/** A render guard: no sane strip needs more lines than this on the belt. */
const MAX_ITEMS = 40;

/**
 * One entry per board that has something queued.
 *
 * Exported and pure because the interesting part is which fixtures get left
 * out, and that is worth testing without a browser.
 *
 * SKIPPED: a board with an empty queue, and a fixture where NEITHER player is
 * known. Boards are assigned for every knockout round when the bracket is
 * generated, so while the quarter-finals are being played a board's next match
 * can be a semi-final nobody can name yet — "Board 3: TBC vs TBC" is a line
 * that tells the room nothing.
 *
 * KEPT: a fixture where one side is known, which is a real thing to announce —
 * the winner of the match in front of you plays whoever that is.
 */
export function tickerEntries(boards = []) {
  const entries = [];

  for (const board of boards) {
    const next = board.upcoming?.[0];
    if (!next) continue;

    const player1 = next.player1_name ?? null;
    const player2 = next.player2_name ?? null;
    if (!player1 && !player2) continue;

    entries.push({
      id:          `${board.boardNumber}-${next.id}`,
      boardNumber: board.boardNumber,
      player1:     player1 ?? 'TBC',
      player2:     player2 ?? 'TBC',
      // A missing marker is not an error worth printing. A bye has none by
      // definition, and a group round where every idle player is already
      // playing legitimately has none either.
      marker:      next.marker_name ?? null,
    });
  }

  return entries;
}

/** The line as it reads on the wall. */
export function entryText(entry) {
  const fixture = `Board ${entry.boardNumber}: ${entry.player1} vs ${entry.player2}`;
  return entry.marker ? `${fixture} — Marker: ${entry.marker}` : fixture;
}

/** Whether this screen has asked for less movement. */
function prefersReducedMotion() {
  return typeof window !== 'undefined'
    && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches === true;
}

/* ------------------------------------------------------------------------- */

export default function TvTicker({ boards }) {
  const entries = useMemo(() => tickerEntries(boards ?? []), [boards]);

  // Read when a line is appended, so the tail always speaks for the CURRENT
  // fixtures. A ref rather than state: nothing should re-render because a
  // fixture changed — the change is picked up by the next line to be queued.
  const latestRef = useRef(entries);
  useEffect(() => { latestRef.current = entries; }, [entries]);

  const [items, setItems] = useState([]);

  const trackRef  = useRef(null);
  const windowRef = useRef(null);
  const offsetRef = useRef(0);   // px the tape has travelled left
  const cursorRef = useRef(0);   // which fixture is next onto the tail
  const seqRef    = useRef(0);   // keys: the same board recurs, positions do not

  const reduced = useMemo(prefersReducedMotion, []);

  /** The next line for the tail, from the fixtures as they stand right now. */
  const nextItem = useCallback(() => {
    const list = latestRef.current;
    if (!list.length) return null;

    const entry = list[cursorRef.current % list.length];
    cursorRef.current += 1;

    return { key: `t${seqRef.current++}`, text: entryText(entry) };
  }, []);

  /**
   * Keep tape queued past the right edge.
   *
   * Runs after every render and appends ONE line, which causes another render
   * and another check — so the belt fills in a few frames at startup and stays
   * topped up as lines are recycled. Appending in a loop here would mean
   * measuring elements that have not been laid out yet.
   */
  useLayoutEffect(() => {
    const track = trackRef.current;
    const view  = windowRef.current;
    if (!track || !view || reduced) return;

    const queuedAhead = track.scrollWidth - offsetRef.current;
    if (queuedAhead >= view.clientWidth + LOOKAHEAD_PX) return;
    if (items.length >= MAX_ITEMS) return;

    const item = nextItem();
    // No fixtures left to queue: the belt runs dry and the strip takes itself
    // off screen once the last line has travelled off, which is a gentler exit
    // than vanishing mid-stream.
    if (item) setItems((prev) => [...prev, item]);
    // entries.length is a dependency because the FIRST fixtures of the night
    // arrive while the belt is empty: without it this would not re-run, and a
    // strip that started empty would stay empty.
  }, [items, entries.length, nextItem, reduced]);

  /** The motion itself. */
  useEffect(() => {
    if (reduced) return undefined;

    let frame = 0;
    let last  = performance.now();

    const step = (now) => {
      // Clamped: a backgrounded tab resumes with a huge delta, and a belt that
      // teleports on wake is the discontinuity this whole design avoids.
      const dt = Math.min(0.1, (now - last) / 1000);
      last = now;

      offsetRef.current += PX_PER_SECOND * dt;

      const track = trackRef.current;
      if (track) {
        const first = track.firstElementChild;

        // Fully past the left edge: drop it and take its width off the offset
        // in the same frame. Same pixels, same places, one fewer element.
        if (first && first.offsetWidth <= offsetRef.current) {
          offsetRef.current -= first.offsetWidth;
          setItems((prev) => prev.slice(1));
        }

        track.style.transform = `translate3d(${-offsetRef.current}px, 0, 0)`;
      }

      frame = requestAnimationFrame(step);
    };

    frame = requestAnimationFrame(step);
    return () => cancelAnimationFrame(frame);
  }, [reduced]);

  // A screen that has asked for less motion gets the fixtures standing still.
  if (reduced) {
    if (!entries.length) return null;

    return (
      <footer className="hd-tv-ticker">
        <span className="hd-tv-ticker-label">Next up</span>
        <div className="hd-tv-ticker-window hd-tv-ticker-static">
          {entries.map((entry) => (
            <span className="hd-tv-ticker-item" key={entry.id}>{entryText(entry)}</span>
          ))}
        </div>
      </footer>
    );
  }

  // Nothing queued anywhere AND nothing still travelling — between the group
  // stage ending and a bracket existing, or at the end of the night.
  //
  // Both halves matter. Hiding on an empty belt alone would never show the
  // strip at all: the queue starts empty, and the effect that fills it needs
  // the track mounted to measure. Hiding on empty fixtures alone would snatch
  // the last line off mid-screen instead of letting it travel off.
  if (!items.length && !entries.length) return null;

  return (
    <footer className="hd-tv-ticker">
      <span className="hd-tv-ticker-label">Next up</span>

      <div className="hd-tv-ticker-window" ref={windowRef}>
        <div className="hd-tv-ticker-track" ref={trackRef}>
          {items.map((item) => (
            <span className="hd-tv-ticker-item" key={item.key}>{item.text}</span>
          ))}
        </div>
      </div>
    </footer>
  );
}
